import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { DialogComponent } from '@theme/dialog';
import { morph } from '@theme/morph';
import { onAnimationEnd, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's add to cart button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} [productImage] - Product image element (desktop only)
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * The bar never owns the variant state: every color/size interaction is
 * proxied to the main `variant-picker` inputs so the whole page (form,
 * gallery, price, URL) stays in sync, and the bar re-renders itself from
 * the resulting ProductSelectEvent morph.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {boolean} */
  #addInFlight = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange, { signal });

    this.#getInitialQuantity();
    this.#hiddenByBottom = false;
    this.#syncVisibilityState();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!isDesktopBreakpoint()) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - show the bar again.
          // On desktop, only do this once the buy buttons are already above the viewport.
          const rect = buyButtonsBlock.getBoundingClientRect();
          const shouldShowOnDesktop = isDesktopBreakpoint() ? (rect.bottom < 0 || rect.top < 0) : true;

          this.#hiddenByBottom = false;

          if (shouldShowOnDesktop && !this.#isChatActive()) {
            this.#showStickyBar();
          } else {
            this.#hideStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar (desktop, or
   * mobile when the product has no size selector).
   */
  handleAddToCartClick = () => {
    this.#addToCart();
  };

  /**
   * Opens the size drawer (mobile trigger button).
   */
  openSizeDrawer = () => {
    this.#getDrawer('size')?.showDialog();
  };

  /**
   * Opens the color drawer (mobile swatches trigger).
   */
  openColorDrawer = () => {
    this.#getDrawer('color')?.showDialog();
  };

  /**
   * Handles a color pick inside the color drawer: applies the color to the
   * main variant picker and closes the drawer.
   * @param {Event} event - The click event
   */
  handleColorSelect = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-option-value-id]') : null;
    if (!(button instanceof HTMLElement) || this.#addInFlight) return;

    this.#selectMainPickerOption(button.dataset.optionValueId ?? '');
    this.#getDrawer('color')?.closeDialog();
  };

  /**
   * Handles a size pick inside the size drawer: applies the size to the main
   * variant picker, waits for the page to sync, then adds to cart and closes
   * the drawer.
   * @param {Event} event - The click event
   */
  handleSizeSelect = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-option-value-id]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled || this.#addInFlight) return;

    this.#addInFlight = true;
    const drawer = this.#getDrawer('size');
    drawer?.setAttribute('data-busy', 'true');

    try {
      const result = await this.#selectAndWaitForUpdate(button.dataset.optionValueId ?? '');

      if (result === 'missing') {
        console.warn('[sticky-add-to-cart] Could not find the selected size in the main variant picker');
      } else if (this.dataset.variantAvailable === 'true') {
        this.#addToCart();
      }
    } finally {
      this.#addInFlight = false;
      drawer?.removeAttribute('data-busy');
      drawer?.closeDialog();
    }
  };

  /**
   * Handles the desktop size dropdown change: applies the size to the main
   * variant picker.
   * @param {Event} event - The change event
   */
  handleSizeSelectChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const option = select.selectedOptions[0];
    if (option?.dataset.optionValueId) {
      this.#selectMainPickerOption(option.dataset.optionValueId);
    }
  };

  /**
   * Clicks the main product form's add to cart button (puppet pattern) and
   * plays the sticky bar feedback animations.
   */
  async #addToCart() {
    const targetButton = this.#targetAddToCartButton;
    if (!targetButton || targetButton.disabled) return;

    targetButton.dataset.puppet = 'true';
    targetButton.click();

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    // Fly-to-cart animation needs the product image, which is desktop-only
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const { productImage } = this.refs;

    if (isDesktopBreakpoint() && cartIcon && productImage) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

      flyToCartElement.classList.add('fly-to-cart--sticky');
      flyToCartElement.style.setProperty('background-image', `url(${productImage.src})`);
      flyToCartElement.useSourceSize = 'true';
      flyToCartElement.source = productImage;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);

      await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    } else {
      await onAnimationEnd([this.refs.addToCartButton]);
    }

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  }

  /**
   * Finds an option value in the main variant picker and selects it,
   * triggering the picker's normal update flow (fetch, events, morphs).
   *
   * @param {string} optionValueId - The option value ID to select
   * @returns {'selected' | 'noop' | 'missing'} Whether a change was triggered
   */
  #selectMainPickerOption(optionValueId) {
    if (!optionValueId) return 'missing';

    const picker = this.#getMainVariantPicker();
    const target = picker?.querySelector(
      `input[data-option-value-id="${optionValueId}"], option[data-option-value-id="${optionValueId}"]`
    );

    if (target instanceof HTMLInputElement) {
      if (target.checked) return 'noop';
      target.click();
      return 'selected';
    }

    if (target instanceof HTMLOptionElement) {
      const select = target.closest('select');
      if (!select) return 'missing';
      if (select.value === target.value) return 'noop';
      select.value = target.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return 'selected';
    }

    return 'missing';
  }

  /**
   * Selects an option value on the main picker and resolves once the
   * resulting variant update has been applied to the page (product form
   * variant input, sticky bar morph, etc).
   *
   * @param {string} optionValueId - The option value ID to select
   * @returns {Promise<'selected' | 'noop' | 'missing'>}
   */
  #selectAndWaitForUpdate(optionValueId) {
    const section = this.closest('.shopify-section');
    if (!section) return Promise.resolve(this.#selectMainPickerOption(optionValueId));

    return new Promise((resolve) => {
      const listenerController = new AbortController();

      /** @param {'selected' | 'noop' | 'missing'} value */
      const finish = (value) => {
        listenerController.abort();
        resolve(value);
      };

      // Attach before selecting: the picker dispatches ProductSelectEvent
      // synchronously from within the click/change handler.
      section.addEventListener(
        StandardEvents.productSelect,
        (event) => {
          const promise = /** @type {ProductSelectEvent} */ (event).promise;

          if (!(promise instanceof Promise)) return finish('selected');

          // Wait one macrotask after resolution so sibling listeners
          // (product form, sticky bar morph) have applied the update.
          promise.then(() => setTimeout(() => finish('selected'), 0)).catch(() => finish('selected'));
        },
        { once: true, signal: listenerController.signal }
      );

      const result = this.#selectMainPickerOption(optionValueId);

      if (result !== 'selected') {
        finish(result);
        return;
      }

      // Safety net in case the update event never settles
      setTimeout(() => finish('selected'), 5000);
    });
  }

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        const { html, productId, resource: variant } = detail ?? {};

        if (productId && productId !== this.dataset.productId) return;

        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html?.querySelector?.('sticky-add-to-cart'));
        const newStickyBar = newStickyAddToCart?.querySelector('[ref="stickyBar"]');
        const variantAvailable = newStickyAddToCart?.dataset.variantAvailable;

        if (newStickyBar && html && !detail?.htmlApplied) {
          // Morph the entire sticky bar content when the server response is
          // the source of truth for the updated markup.
          morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

          // Refresh drawer contents. They are kept outside the bar so morphing
          // them doesn't close an open <dialog> mid-interaction.
          for (const content of this.querySelectorAll('[data-sticky-drawer-content]')) {
            const key = content.getAttribute('data-sticky-drawer-content');
            const newContent = newStickyAddToCart?.querySelector(`[data-sticky-drawer-content="${key}"]`);
            if (newContent) morph(content, newContent, { childrenOnly: true });
          }
        }

        // The <select> keeps its live value; align it with the newly rendered
        // selected option after the update, whether the bar was morphed or the
        // combined-listing flow already applied the markup in place.
        const sizeSelect = this.refs.stickyBar.querySelector('.sticky-add-to-cart__size-select');
        if (sizeSelect instanceof HTMLSelectElement) {
          const selectedOption = sizeSelect.querySelector('option[selected]');
          if (selectedOption instanceof HTMLOptionElement) {
            sizeSelect.value = selectedOption.value;
          }
        }

        this.#hiddenByBottom = false;
        this.#syncVisibilityState();
        this.dataset.variantAvailable = variantAvailable ?? this.dataset.variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const variantPicker = this.#getMainVariantPicker();
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {Event} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    const { detail } = /** @type {QuantitySelectorUpdateEvent} */ (event);

    // Only respond to product page quantity selector updates, not cart drawer
    if (detail.cartLine) return;

    this.#currentQuantity = detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Closes the mobile drawers when resizing into the desktop breakpoint,
   * where the size dropdown replaces them.
   */
  #handleBreakpointChange = () => {
    if (!isDesktopBreakpoint()) {
      this.#hiddenByBottom = false;
      this.#syncVisibilityState();
      return;
    }

    this.#getDrawer('size')?.closeDialog();
    this.#getDrawer('color')?.closeDialog();
  };

  /**
   * Re-evaluates the sticky bar visibility based on the current viewport,
   * footer position, and chat availability.
   */
  #syncVisibilityState() {
    if (this.#isChatActive()) {
      this.#hideStickyBar();
      return;
    }

    if (!isDesktopBreakpoint()) {
      if (this.#hiddenByBottom) {
        this.#hideStickyBar();
      } else {
        this.#showStickyBar();
      }
      return;
    }

    if (this.#isStuck) {
      this.#showStickyBar();
    } else {
      this.#hideStickyBar();
    }
  }

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the main variant picker for this product in the same section
   * @returns {Element | null}
   */
  #getMainVariantPicker() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    return this.closest('.shopify-section')?.querySelector(`variant-picker[data-product-id="${productId}"]`) ?? null;
  }

  /**
   * Gets one of the sticky bar's drawers
   * @param {'color' | 'size'} kind - The drawer to fetch
   * @returns {DialogComponent | null}
   */
  #getDrawer(kind) {
    const drawer = this.querySelector(`dialog-component[data-sticky-drawer="${kind}"]`);
    return drawer instanceof DialogComponent ? drawer : null;
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
